import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import { useI18n } from '../i18n/index.jsx';
import { startImpersonation } from '../utils/impersonation';

export default function SuperAdminSelect() {
  const { user, setUser, setPage, setCurrentCompany } = useApp();
  const { t } = useI18n();
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [clientsOpen, setClientsOpen] = useState(false);
  const [clientsQuery, setClientsQuery] = useState('');
  const [viewMode, setViewMode] = useState('admin'); // 'admin' or 'client'

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [companiesRes, clientsRes] = await Promise.all([
        api.get('/companies'),
        api.get('/admin/clients').catch(() => ({ data: [] }))
      ]);
      setCompanies(companiesRes.data || []);
      const rawClients = Array.isArray(clientsRes.data) ? clientsRes.data : [];
      const onlyClients = rawClients.filter(c => c.role === 'client' && c.status !== 'blocked');
      const dedupedMap = new Map();
      for (const c of onlyClients) {
        const key = c.telegram_id || `${(c.first_name||'').trim().toLowerCase()}|${(c.last_name||'').trim().toLowerCase()}|${(c.telegram_username||'').trim().toLowerCase()}`;
        const prev = dedupedMap.get(key);
        const cScore = (c.telegram_id ? 1000 : 0) + (c.has_paid_access ? 100 : 0) + (c.onboarding_step || 0) + (c.status === 'active' ? 10 : 0) + c.id/10000;
        const pScore = prev ? ((prev.telegram_id ? 1000 : 0) + (prev.has_paid_access ? 100 : 0) + (prev.onboarding_step || 0) + (prev.status === 'active' ? 10 : 0) + prev.id/10000) : -1;
        if (!prev || cScore > pScore) {
          dedupedMap.set(key, c);
        }
      }
      setClients(Array.from(dedupedMap.values()));
    } catch (e) {
      console.error('Failed to load data:', e);
    }
    setLoading(false);
    setClientsLoading(false);
  }

  async function enterAsManager(company) {
    // Client mode: enter company as manager (not full admin)
    localStorage.setItem('superadmin_as_client', 'true');
    localStorage.removeItem('admin_user');
    setUser(u => ({
      ...u,
      _force_client_mode: true,
      _original_role: u.role,
    }));
    setCurrentCompany(company);
    setPage('dashboard');
  }

  async function enterIncubator() {
    // Incubator defaults to NOTO company scope for owner-mode tools
    await enterCompany({ id: 1, name: 'NOTO RECRUT SP. Z O.O.', nip: '5272895203' });
  }

  async function enterCompany(company) {
    try {
      const res = await api.post(`/admin/impersonate/company/${company.id}`);
      startImpersonation({
        token: res.data.access_token || res.data.token,
        mode: 'company_owner',
        companyId: company.id,
        adminUser: user,
      });
      window.location.reload();
    } catch (e) {
      // fallback (legacy): local switch
      setUser(u => ({
        ...u,
        _original_account_type: u._original_account_type || u.account_type,
        _original_role: u._original_role || u.role,
        account_type: 'own_company',
        role: 'company_owner',
      }));
      setCurrentCompany(company);
      setPage('dashboard');
    }
  }

  async function impersonateClient(clientId) {
    try {
      const res = await api.post(`/admin/impersonate/${clientId}`);
      
      startImpersonation({
        token: res.data.access_token || res.data.token,
        mode: 'user',
        adminUser: user,
      });

      // Reload to apply new token
      window.location.reload();
    } catch (e) {
      setMsg('❌ ' + (e.userMessage || e.response?.data?.detail || 'Ошибка импеpersonации'));
      setTimeout(() => setMsg(''), 3000);
    }
  }


  const filteredClients = clients.filter(c => {
    const q = clientsQuery.trim().toLowerCase();
    if (!q) return true;
    const name = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
    const uname = (c.telegram_username || '').toLowerCase();
    const phone = (c.phone || '').toLowerCase();
    return name.includes(q) || uname.includes(q) || phone.includes(q) || String(c.id).includes(q);
  });

  return (
    <div className="page" style={{ padding: '20px 16px', minHeight: '100vh', background: '#0f0f0f' }}>
      {/* Mode Selection */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
        <button onClick={() => {
          setViewMode('client');
          localStorage.removeItem('admin_user');
          localStorage.setItem('superadmin_as_client', 'true');
        }} style={{
          padding: '16px 12px', borderRadius: 14, cursor: 'pointer',
          background: viewMode === 'client' ? 'linear-gradient(135deg, rgba(59,130,246,0.3), rgba(37,99,235,0.2))' : 'linear-gradient(135deg, rgba(59,130,246,0.1), rgba(37,99,235,0.05))',
          border: viewMode === 'client' ? '2px solid rgba(59,130,246,0.5)' : '1px solid rgba(59,130,246,0.2)',
          textAlign: 'center', transition: 'all 0.2s',
        }}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>👤</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#60a5fa' }}>Как клиент</div>
          <div style={{ fontSize: 11, color: 'rgba(96,165,250,0.6)', marginTop: 4 }}>Менеджер фирмы</div>
        </button>
        <button onClick={() => {
          setViewMode('admin');
          localStorage.removeItem('superadmin_as_client');
          setUser(u => ({ ...u, _force_client_mode: false }));
        }} style={{
          padding: '16px 12px', borderRadius: 14, cursor: 'pointer',
          background: viewMode === 'admin' ? 'linear-gradient(135deg, rgba(245,158,11,0.3), rgba(217,119,6,0.2))' : 'linear-gradient(135deg, rgba(245,158,11,0.1), rgba(217,119,6,0.05))',
          border: viewMode === 'admin' ? '2px solid rgba(245,158,11,0.5)' : '1px solid rgba(245,158,11,0.2)',
          textAlign: 'center', transition: 'all 0.2s',
        }}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>👑</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fbbf24' }}>Как админ</div>
          <div style={{ fontSize: 11, color: 'rgba(251,191,36,0.6)', marginTop: 4 }}>Полный доступ</div>
        </button>
      </div>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>👑</div>
        <h1 style={{ 
          fontSize: 28, 
          fontWeight: 800, 
          margin: '0 0 8px', 
          color: '#fff',
          background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          {t('super_admin.title')}
        </h1>
        <p style={{ opacity: 0.6, fontSize: 15, margin: 0, color: '#a1a1aa' }}>
          {t('super_admin.select_mode')}
        </p>
      </div>

      {msg && (
        <div style={{
          padding: '12px 16px',
          marginBottom: 20,
          borderRadius: 12,
          background: msg.startsWith('❌') ? 'rgba(239, 68, 68, 0.1)' : 'rgba(34, 197, 94, 0.1)',
          border: `1px solid ${msg.startsWith('❌') ? 'rgba(239, 68, 68, 0.3)' : 'rgba(34, 197, 94, 0.3)'}`,
          color: msg.startsWith('❌') ? '#ef4444' : '#22c55e',
          fontSize: 14,
        }}>
          {msg}
        </div>
      )}

      {/* Companies Section */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ 
          fontSize: 18, 
          fontWeight: 700, 
          color: '#fff', 
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          🏢 {viewMode === 'client' ? 'Выберите фирму (менеджер)' : t('super_admin.enter_as_owner')}
        </div>

        {loading ? (
          <div style={{ 
            textAlign: 'center', 
            padding: 32, 
            opacity: 0.6,
            color: '#a1a1aa'
          }}>
            {t('super_admin.loading')}
          </div>
        ) : companies.length === 0 ? (
          <div style={{
            textAlign: 'center', 
            padding: '32px 20px',
            background: 'rgba(255,255,255,0.03)', 
            borderRadius: 16,
            border: '1px solid rgba(255,255,255,0.08)',
          }}>
            <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.4 }}>🏢</div>
            <p style={{ opacity: 0.6, fontSize: 15, margin: 0, color: '#a1a1aa' }}>
              {t('super_admin.no_companies')}
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {companies.map(company => (
              <button 
                key={company.id} 
                onClick={() => viewMode === 'client' ? enterAsManager(company) : enterCompany(company)} 
                style={{
                  width: '100%', 
                  padding: '18px 20px', 
                  borderRadius: 16, 
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(5,150,105,0.1))',
                  border: '1px solid rgba(16,185,129,0.25)',
                  textAlign: 'left',
                  boxShadow: '0 4px 16px rgba(16,185,129,0.08)',
                  transition: 'all 0.2s',
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 24px rgba(16,185,129,0.15)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 16px rgba(16,185,129,0.08)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{
                    width: 52, 
                    height: 52, 
                    borderRadius: 14,
                    background: 'linear-gradient(135deg, rgba(16,185,129,0.3), rgba(5,150,105,0.2))',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: 24, 
                    flexShrink: 0,
                  }}>
                    🏢
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ 
                      fontSize: 16, 
                      fontWeight: 700, 
                      color: '#10b981', 
                      marginBottom: 4,
                      lineHeight: 1.2
                    }}>
                      {company.short_name || company.name}
                    </div>
                    {company.nip && (
                      <div style={{ 
                        fontSize: 13, 
                        color: 'rgba(16,185,129,0.7)',
                        fontFamily: 'monospace'
                      }}>
                        NIP: {company.nip}
                      </div>
                    )}
                  </div>
                  <div style={{ 
                    fontSize: 20, 
                    color: 'rgba(16,185,129,0.5)', 
                    flexShrink: 0 
                  }}>
                    →
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Incubator Section */}
      <div>
        <div style={{ 
          fontSize: 18, 
          fontWeight: 700, 
          color: '#fff', 
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          🏗 {t('super_admin.incubator')}
        </div>

        {/* Incubator Mode Button */}
        <button 
          onClick={enterIncubator} 
          style={{
            width: '100%', 
            padding: '20px 20px', 
            borderRadius: 16, 
            cursor: 'pointer',
            background: 'linear-gradient(135deg, rgba(99,102,241,0.25), rgba(139,92,246,0.15))',
            border: '1px solid rgba(99,102,241,0.3)',
            marginBottom: 20, 
            textAlign: 'left',
            boxShadow: '0 4px 16px rgba(99,102,241,0.1)',
            transition: 'all 0.2s',
          }}
          onMouseOver={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 8px 24px rgba(99,102,241,0.2)';
          }}
          onMouseOut={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 16px rgba(99,102,241,0.1)';
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 56, 
              height: 56, 
              borderRadius: 16,
              background: 'linear-gradient(135deg, rgba(99,102,241,0.4), rgba(139,92,246,0.3))',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              fontSize: 28, 
              flexShrink: 0,
            }}>
              🏗
            </div>
            <div>
              <div style={{ 
                fontSize: 18, 
                fontWeight: 800, 
                color: '#a5b4fc', 
                marginBottom: 6 
              }}>
                Бизнес-инкубатор
              </div>
              <div style={{ 
                fontSize: 14, 
                color: 'rgba(165,180,252,0.7)', 
                lineHeight: 1.4 
              }}>
                {t('super_admin.incubator_desc')}
              </div>
            </div>
          </div>
        </button>

        {/* Clients List */}
        <div>
          <div style={{ 
            fontSize: 16, 
            fontWeight: 600, 
            color: 'rgba(255,255,255,0.8)', 
            marginBottom: 12,
            paddingLeft: 4
          }}>
            👥 Клиенты ({clients.length})
          </div>

          <button onClick={() => setClientsOpen(v => !v)} style={{width:'100%', marginBottom:10, padding:'10px 12px', borderRadius:10, border:'1px solid rgba(255,255,255,0.15)', background:'rgba(255,255,255,0.04)', color:'#fff', textAlign:'left', cursor:'pointer'}}>
            {clientsOpen ? '▼ Свернуть список клиентов' : '▶ Открыть список клиентов'}
          </button>

          {clientsOpen && <input value={clientsQuery} onChange={(e)=>setClientsQuery(e.target.value)} placeholder="Поиск: имя / @username / телефон / ID" style={{width:'100%', marginBottom:10, padding:'10px 12px', borderRadius:10, background:'rgba(255,255,255,0.04)', color:'#fff', border:'1px solid rgba(255,255,255,0.12)'}} />}

          {clientsLoading ? (
            <div style={{ 
              textAlign: 'center', 
              padding: 24, 
              opacity: 0.6,
              color: '#a1a1aa'
            }}>
              Загрузка клиентов...
            </div>
          ) : filteredClients.length === 0 ? (
            <div style={{
              textAlign: 'center', 
              padding: '24px 20px',
              background: 'rgba(255,255,255,0.03)', 
              borderRadius: 14,
              border: '1px solid rgba(255,255,255,0.08)',
            }}>
              <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.4 }}>👥</div>
              <p style={{ opacity: 0.6, fontSize: 14, margin: 0, color: '#a1a1aa' }}>
                Нет клиентов в системе
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {filteredClients.map(client => (
                <button 
                  key={client.id} 
                  onClick={() => impersonateClient(client.id)} 
                  style={{
                    width: '100%', 
                    padding: '16px 18px', 
                    borderRadius: 12, 
                    cursor: 'pointer',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    textAlign: 'left',
                    transition: 'all 0.15s',
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = 'rgba(255,255,255,0.08)';
                    e.target.style.borderColor = 'rgba(255,255,255,0.2)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = 'rgba(255,255,255,0.04)';
                    e.target.style.borderColor = 'rgba(255,255,255,0.1)';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 40, 
                      height: 40, 
                      borderRadius: 10,
                      background: 'linear-gradient(135deg, rgba(168,85,247,0.2), rgba(147,51,234,0.15))',
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      fontSize: 18, 
                      flexShrink: 0,
                    }}>
                      👤
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ 
                        fontSize: 15, 
                        fontWeight: 600, 
                        color: '#fff', 
                        marginBottom: 2 
                      }}>
                        {client.first_name} {client.last_name}
                      </div>
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', fontFamily: 'monospace' }}>@{client.telegram_username || `user${client.id}`}</div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>ID:{client.id} • step:{client.onboarding_step || 0} • {client.has_paid_access ? 'paid' : 'free'}</div>
                    </div>
                    <div style={{ 
                      fontSize: 16, 
                      color: 'rgba(255,255,255,0.3)', 
                      flexShrink: 0 
                    }}>
                      →
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}